/**
 * Ryan O'Dowd
 * 2024-02-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import EmptyScreen from '@components/common/EmptyScreen';
import {
  READ_ROUTE,
} from '@utils/store/actions';
import ReadingHistoryDialog from '@components/ReadingHistoryDialog';
import Text from '@components/common/Text';
import WidgetBase from '@components/WidgetBase';
import getStyles from './styles';
import hooks from '@hooks';

const ContinueReading = (props: ContinueReadingProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const [_isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);

  const readingHistory = hooks.useTypedSelector((state) => state.readingHistory);
  const preferredTranslation = hooks.usePreferredTranslation();

  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
    >
      <WidgetBase.Header
        title={i18n('continueReading')}
        onPress={() => navigation.navigate(READ_ROUTE)}
        rightIconName={!readingHistory.length ? null : 'cog'}
        onPressRightIcon={!readingHistory.length ? null : () => setIsHistoryDialogOpen(true)}
      />
      <WidgetBase.Content
        noBackground={readingHistory.length > 0}
      >
        {readingHistory.length === 0
          ? (
            <EmptyScreen.Wrapper>
              <EmptyScreen.Text>{i18n('recentlyReadChapters')}</EmptyScreen.Text>
              <Button
                mode='contained'
                theme={styles.colors.accent}
                onPress={() => navigation.navigate(READ_ROUTE)}
              >
                {i18n('readNow')}
              </Button>
            </EmptyScreen.Wrapper>
          ) : (
            readingHistory.map((chapterHistory) => {
              const chapter = CPassage.getChapterById(chapterHistory.chapterId);
              const bookName = CPassage.getBookName(preferredTranslation.id, chapter.book_id);
              return (
                <TouchableOpacity// @TODO: convert to ListItem with prop for translucent background and no border (other widgets too)
                  key={chapterHistory.chapterId}
                  style={[styles.options, styles.readingHistoryOption]}
                  onPress={() => navigation.navigate(READ_ROUTE, {
                    chapterId: chapterHistory.chapterId,
                  })}
                >
                  <View style={styles.optionTextWrapper}>
                    <Text style={styles.chapterHistoryText}>{`${bookName} ${chapter.chapter_number}`}</Text>
                  </View>
                </TouchableOpacity>
              );
            })
          )
        }
        {_isHistoryDialogOpen && (
          <ReadingHistoryDialog
            onClose={() => setIsHistoryDialogOpen(false)}
            onSelect={(chapterId) => {
              navigation.navigate(READ_ROUTE, {chapterId});
              setIsHistoryDialogOpen(false);
            }}
          />
        )}
      </WidgetBase.Content>
    </WidgetBase.Container>
  );
};

class ContinueReadingProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;
};

export default ContinueReading;
